import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../layout/Navbar';
import '../../styles/dashboard.css';
import { collection, query, where, orderBy, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Messages = () => {
  const { user, userData } = useAuth();
  const params = useQuery();
  const otherId = params.get('user');

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  // conversationId is symmetric so both users share same id
  const conversationId = user && otherId ? [user.uid, otherId].sort().join('_') : null;

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const q = queryFn(conversationId);
    const unsub = onSnapshot(q, snapshot => {
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(msgs);
      setLoading(false);
    }, err => {
      console.error('Messages listener error', err);
      setLoading(false);
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  function queryFn(convId) {
    return query(
      collection(db, 'contacts'),
      where('conversationId', '==', convId),
      orderBy('createdAt', 'asc')
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    if (!user || !otherId || !text.trim()) return;

    try {
      await addDoc(collection(db, 'contacts'), {
        conversationId,
        fromId: user.uid,
        toId: otherId,
        text: text.trim(),
        createdAt: serverTimestamp()
      });
      setText('');
    } catch (err) {
      console.error('Failed to send message', err);
      alert('Failed to send message.');
    }
  };

  if (!user) {
    return (
      <div className="dashboard-root">
        <Navbar />
        <div className="dashboard-container">
          <div className="dashboard-main">
            <div className="empty-state">Please log in to use messages.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <Navbar />
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="dashboard-header">
            <h1>Messages</h1>
            <p>Contact other users about bookings and sessions.</p>
          </div>

          <div className="messages-panel">
            {!otherId ? (
              <div className="empty-state">
                <p>Select a conversation or open a student's profile to message them.</p>
                <p>You can also start a conversation by adding <code>?user=USER_ID</code> to the URL.</p>
              </div>
            ) : (
              <div className="conversation">
                <div className="conversation-messages">
                  {loading ? (
                    <div className="loading">Loading...</div>
                  ) : messages.length === 0 ? (
                    <div className="empty-state">No messages yet. Say hello!</div>
                  ) : (
                    messages.map(m => (
                      <div key={m.id} className={`msg ${m.fromId === user.uid ? 'outgoing' : 'incoming'}`}>
                        <div className="msg-text">{m.text}</div>
                        <div className="msg-meta">{m.createdAt?.toDate ? m.createdAt.toDate().toLocaleString() : ''}</div>
                      </div>
                    ))
                  )}
                </div>

                <form className="message-form" onSubmit={handleSend}>
                  <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Write a message..." />
                  <button className="btn-primary" type="submit">Send</button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
